/**
 * Hexagonal grid pathfinding utilities
 * Uses cube coordinates (q, r, s) where q + r + s = 0
 */

export interface HexCoord {
  q: number;
  r: number;
  s: number;
}

/**
 * Normalize a hex coordinate to ensure s is defined
 * In cube coordinates: q + r + s = 0, so s = -q - r
 */
export function normalizeHexCoord(coord: { q: number; r: number; s?: number }): HexCoord {
  return {
    q: coord.q,
    r: coord.r,
    s: coord.s !== undefined ? coord.s : -coord.q - coord.r,
  };
}

/**
 * Calculate the distance between two hexes (number of steps)
 */
export function hexDistance(a: HexCoord, b: HexCoord): number {
  return (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(a.s - b.s)) / 2;
}

/**
 * Get all 6 neighbors of a hex
 */
export function hexNeighbors(hex: HexCoord): HexCoord[] {
  const directions = [
    { q: 1, r: 0, s: -1 },   // East
    { q: 1, r: -1, s: 0 },   // Northeast
    { q: 0, r: -1, s: 1 },   // Northwest
    { q: -1, r: 0, s: 1 },   // West
    { q: -1, r: 1, s: 0 },   // Southwest
    { q: 0, r: 1, s: -1 },   // Southeast
  ];
  
  return directions.map(dir => ({
    q: hex.q + dir.q,
    r: hex.r + dir.r,
    s: hex.s + dir.s,
  }));
}

/**
 * Linear interpolation between two hexes
 * Used for smooth pathfinding
 */
function hexLerp(a: HexCoord, b: HexCoord, t: number): { q: number; r: number; s: number } {
  return {
    q: a.q + (b.q - a.q) * t,
    r: a.r + (b.r - a.r) * t,
    s: a.s + (b.s - a.s) * t,
  };
}

/**
 * Round floating point cube coordinates to nearest hex
 */
function hexRound(hex: { q: number; r: number; s: number }): HexCoord {
  let q = Math.round(hex.q);
  let r = Math.round(hex.r);
  let s = Math.round(hex.s);
  
  const qDiff = Math.abs(q - hex.q);
  const rDiff = Math.abs(r - hex.r);
  const sDiff = Math.abs(s - hex.s);
  
  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  } else {
    s = -q - r;
  }
  
  return { q, r, s };
}

/**
 * Find the shortest path between two hexes using line drawing
 * This is simple and efficient for hex grids (no obstacles for now)
 */
export function findHexPath(start: HexCoord | { q: number; r: number; s?: number }, end: HexCoord | { q: number; r: number; s?: number }): HexCoord[] {
  // Normalize coordinates to ensure s is defined
  const normalizedStart = normalizeHexCoord(start);
  const normalizedEnd = normalizeHexCoord(end);
  
  const distance = hexDistance(normalizedStart, normalizedEnd);
  
  if (distance === 0) {
    return [normalizedStart];
  }
  
  const path: HexCoord[] = [];
  
  // Use linear interpolation to draw a line
  for (let i = 0; i <= distance; i++) {
    const t = i / distance;
    const hex = hexLerp(normalizedStart, normalizedEnd, t);
    const roundedHex = hexRound(hex);
    
    // Avoid duplicates
    if (path.length === 0 || 
        path[path.length - 1].q !== roundedHex.q ||
        path[path.length - 1].r !== roundedHex.r) {
      path.push(roundedHex);
    }
  }
  
  return path;
}

/**
 * Check if two hex coordinates are equal
 * Handles coordinates with or without s (calculates s if missing)
 */
export function hexEqual(a: HexCoord | { q: number; r: number; s?: number }, b: HexCoord | { q: number; r: number; s?: number }): boolean {
  const normalizedA = normalizeHexCoord(a);
  const normalizedB = normalizeHexCoord(b);
  return normalizedA.q === normalizedB.q && normalizedA.r === normalizedB.r && normalizedA.s === normalizedB.s;
}

